import {
  Popover,
  Paper,
  Box,
  Typography,
  TextField,
  Slider,
  InputAdornment,
  Grid,
} from "@mui/material";
import React, { useState } from "react";

import { SafeArgonButton } from "0.Mine/4.Argon/ArgonComponents";

interface ValueDialogProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onSave: (value: number, probability: number) => void;
  currentValue: number;
  currentProbability: number;
}

const ValueDialog: React.FC<ValueDialogProps> = ({
  anchorEl,
  open,
  onClose,
  onSave,
  currentValue,
  currentProbability = 50,
}) => {
  // Change to string type to allow empty input
  const [value, setValue] = useState<string>(currentValue > 0 ? currentValue.toString() : "");
  const [probability, setProbability] = useState<number>(currentProbability);

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;

    // Allow empty string or only numeric values
    if (inputValue === "" || /^\d*\.?\d*$/.test(inputValue)) {
      setValue(inputValue);
    }
  };

  const handleProbabilityChange = (_event: Event, newValue: number | number[]) => {
    setProbability(newValue as number);
  };

  const handleSave = () => {
    // Convert empty string to 0 only when saving
    const numericValue = value === "" ? 0 : parseFloat(value);
    onSave(numericValue, probability);
    onClose();
  };

  // Handle keydown to prevent invalid characters
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow: backspace, delete, tab, escape, enter, decimal point
    const allowedKeys = ["Backspace", "Delete", "Tab", "Escape", "Enter", "."];

    // Allow navigation keys
    const navigationKeys = ["ArrowLeft", "ArrowRight", "Home", "End"];

    // Block if not a number and not an allowed key
    if (
      !/\d/.test(event.key) &&
      !allowedKeys.includes(event.key) &&
      !navigationKeys.includes(event.key) &&
      !event.ctrlKey // Allow copy/paste shortcuts
    ) {
      event.preventDefault();
    }

    // Prevent multiple decimal points
    if (event.key === "." && value.includes(".")) {
      event.preventDefault();
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <Paper sx={{ width: 300, p: 2 }}>
        <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
          Valore e Probabilità
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField
              label="Valore"
              fullWidth
              value={value}
              onChange={handleValueChange}
              onKeyDown={handleKeyDown}
              // Change from "number" to "text" to allow more control
              type="text"
              InputProps={{
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
                inputMode: "decimal", // Show numeric keyboard on mobile
              }}
              placeholder="Inserisci valore"
            />
          </Grid>
        </Grid>

        <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
          Probabilita di chiusura: {probability}%
        </Typography>

        <Box sx={{ px: 1, mb: 2 }}>
          <Slider
            value={probability}
            onChange={handleProbabilityChange}
            aria-labelledby="probability-slider"
            valueLabelDisplay="auto"
            step={10}
            marks
            min={0}
            max={100}
          />
        </Box>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <SafeArgonButton size="small" onClick={onClose} sx={{ mr: 1 }}>
            Cancella
          </SafeArgonButton>
          <SafeArgonButton size="small" color="primary" onClick={handleSave}>
            Salva
          </SafeArgonButton>
        </Box>
      </Paper>
    </Popover>
  );
};

export default ValueDialog;
